import { HitTestResult } from '@chartv2/engine/hit-test';
import React from 'react';

import type {
  ChartV2LegendMoreItem,
  ChartV2ResolvedSeries,
} from '../types/chart-v2.types';
import styles from './Legend.module.scss';
import { LegendItem } from './LegendItem';

/** Single row passed to `<Legend>`. */
export interface LegendEntry {
  /** Resolved series — includes the colour the canvas drew with. */
  series: ChartV2ResolvedSeries;
  /** When true, the row renders muted to indicate the series is hidden. */
  hidden?: boolean;
  /**
   * Items rendered in the row's three-dots more-menu. Pass an empty
   * array to suppress the more-menu button entirely.
   */
  moreMenuItems?: ChartV2LegendMoreItem[];
}

/** Localisable strings for `<Legend>`. */
export interface LegendLocales {
  /** Tooltip on the three-dots more-menu button. */
  more?: string;
  /** Tooltip on the hide button (when the row is visible). */
  hide?: string;
  /** Tooltip on the show button (when the row is hidden). */
  show?: string;
  /** Tooltip on the per-row remove (×) button. */
  remove?: string;
  /** Rendered when `entries` is empty. */
  empty?: string;
}

/**
 * Props for `<Legend>`.
 *
 * Required: `entries`. Toggle / remove behaviour is opt-in via callbacks.
 */
export interface LegendProps {
  /** Rows to render. */
  entries: LegendEntry[];
  /** Called when the user clicks the show / hide button on a row. */
  onToggle?: (seriesId: string, isCurrentlyHidden: boolean) => void;
  /**
   * Called when the user clicks the per-row remove (×) button. Omit
   * the prop to hide the remove button entirely.
   */
  onRemove?: (seriesId: string) => void;
  /** String overrides — see `LegendLocales`. */
  locales?: LegendLocales;
  /** @deprecated Use `locales.empty` instead. */
  emptyMessage?: string;
  pointHover?: PointHover | null;
}

export interface PointHover {
  time: number;
  hits: HitTestResult[];
}

/**
 * Legend list for chartv2 — one row per series, with show/hide,
 * remove, and a per-row more-menu (e.g. change colour, move to pane).
 *
 * Toggle and remove behaviour is opt-in via callbacks: omit `onRemove`
 * to hide the × button; omit `onToggle` to make rows non-toggleable.
 * Per-row more-menu items are configured per `LegendEntry`.
 */
export const Legend = ({
  entries,
  onToggle,
  onRemove,
  locales,
  emptyMessage,
  pointHover,
}: LegendProps) => {
  const empty = locales?.empty ?? emptyMessage;

  if (entries.length === 0) {
    return (
      <div className={styles.root} data-testid="chartv2-legend">
        {empty && <span className={styles.empty}>{empty}</span>}
      </div>
    );
  }

  return (
    <ul className={styles.root} data-testid="chartv2-legend">
      {entries.map(entry => (
        <LegendItem
          key={entry.series.id}
          entry={entry}
          onToggle={onToggle}
          onRemove={onRemove}
          locales={locales}
          hoverHit={pointHover?.hits.find(
            hit => hit.seriesId === entry.series.id
          )}
        />
      ))}
    </ul>
  );
};
